import React from "react";
import { useTheme } from "../context/ThemeContext";

type Row = (string | number | React.ReactNode)[];

export type ModalItemsGridProps = {
  headers: string[];
  colWidths: string[];
  rows: Row[];

  // ✅ modo antigo (single) — agora opcional (compat)
  selectedIndex?: number | null;
  onSelectIndex?: (index: number) => void;

  // ✅ modo novo (multi)
  selectedIndexes?: number[]; // ex: [0,2,5]
  onRowClick?: (index: number, ev: React.MouseEvent) => void;

  perColumnRender?: Record<
    string,
    (uiRow: Row, rowIndex: number) => React.ReactNode
  >;
  centerCols?: number[];

  styleOverrides?: {
    container?: React.CSSProperties;
    inner?: React.CSSProperties;
    headerRow?: React.CSSProperties;
    headerCell?: React.CSSProperties;
    row?: React.CSSProperties;
    rowSelected?: React.CSSProperties;
    cell?: React.CSSProperties;
  };

  enableCellTitle?: boolean;
};

// helper pra usar primary como rgba
const hexToRgba = (hex: string | undefined, alpha: number): string => {
  if (!hex) return `rgba(18, 66, 128, ${alpha})`;
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return `rgba(18, 66, 128, ${alpha})`;

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if ([r, g, b].some((v) => Number.isNaN(v))) {
    return `rgba(18, 66, 128, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, alpha)`.replace("alpha", String(alpha));
};

const defaultStyles = {
  container: {
    width: "100%",
    overflowX: "auto",
    fontFamily: "Inter, Arial, sans-serif",
    border: "1px solid #000",
    borderRadius: 0,
    background: "#fff",
  } as React.CSSProperties,
  inner: {
    minWidth: 1080,
  } as React.CSSProperties,
  headerRow: {
    display: "grid",
    padding: 0,
    background: "#124280",
    color: "#fff",
    borderBottom: "1px solid #000",
  } as React.CSSProperties,
  headerCell: {
    padding: "10px 12px",
    fontWeight: 500,
    fontSize: 14,
    borderRight: "1px solid #000",
    whiteSpace: "nowrap" as const,
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  row: {
    display: "grid",
    padding: 0,
    background: "#ffffff",
    cursor: "pointer",
    userSelect: "none",
    borderBottom: "1px solid #000",
    transition: "background 120ms ease",
  } as React.CSSProperties,
  rowSelected: {
    background: "#e6f0ff",
    boxShadow: "inset 0 0 0 1px #4B6CC5FF",
  } as React.CSSProperties,
  cell: {
    padding: "10px 12px",
    fontSize: 14,
    fontWeight: 400,
    borderRight: "1px solid #000",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
};

// Evita title feio quando é ReactNode
function safeTitle(v: any): string | undefined {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return String(v);
  // React element / object -> não tenta serializar
  return undefined;
}

const ModalItemsGrid: React.FC<ModalItemsGridProps> = ({
  headers,
  colWidths,
  rows,
  selectedIndex = null,
  onSelectIndex,
  selectedIndexes,
  onRowClick,
  perColumnRender,
  centerCols,
  styleOverrides,
  enableCellTitle = true,
}) => {
  const { theme } = useTheme();
  const primaryColor = theme?.primary;

  const gridTemplateColumns = React.useMemo(
    () => colWidths.join(" "),
    [colWidths]
  );

  const centerSet = React.useMemo(
    () => new Set(centerCols ?? [0, 1, 3, 4, 5, 7, 8]),
    [centerCols]
  );

  const selectedBg = hexToRgba(primaryColor, 0.18);
  const hoverBg = hexToRgba(primaryColor, 0.08);
  const selectedBorder = hexToRgba(primaryColor, 0.85);

  // ✅ Set final de selecionados:
  // - se veio selectedIndexes => multi
  // - senão usa selectedIndex => single
  const selectedSet = React.useMemo(() => {
    if (Array.isArray(selectedIndexes)) return new Set(selectedIndexes);
    return selectedIndex == null ? new Set<number>() : new Set([selectedIndex]);
  }, [selectedIndexes, selectedIndex]);

  const handleRowClick = React.useCallback(
    (i: number) => (ev: React.MouseEvent) => {
      // ✅ modo novo (pai controla ctrl/shift)
      if (onRowClick) return onRowClick(i, ev);

      // ✅ fallback modo antigo
      onSelectIndex?.(i);
    },
    [onRowClick, onSelectIndex]
  );

  return (
    <div
      style={{
        ...defaultStyles.container,
        ...(styleOverrides?.container ?? {}),
      }}
    >
      <div style={{ ...defaultStyles.inner, ...(styleOverrides?.inner ?? {}) }}>
        {/* Cabeçalho */}
        <div
          style={{
            ...defaultStyles.headerRow,
            gridTemplateColumns,
            background: primaryColor,
            ...(styleOverrides?.headerRow ?? {}),
          }}
          role="row"
        >
          {headers.map((h, idx) => (
            <div
              key={h + idx}
              role="columnheader"
              style={{
                ...defaultStyles.headerCell,
                textAlign: centerSet.has(idx) ? "center" : "left",
                borderRight:
                  idx === headers.length - 1
                    ? "none"
                    : defaultStyles.headerCell.borderRight,
                ...(styleOverrides?.headerCell ?? {}),
              }}
              title={String(h)}
            >
              {h}
            </div>
          ))}
        </div>

        {/* Linhas */}
        {rows.map((r, i) => {
          const selected = selectedSet.has(i);

          return (
            <div
              key={i}
              role="row"
              onClick={handleRowClick(i)}
              style={{
                ...defaultStyles.row,
                gridTemplateColumns,
                ...(selected
                  ? {
                      ...defaultStyles.rowSelected,
                      background: selectedBg,
                      boxShadow: `inset 0 0 0 1px ${selectedBorder}`,
                    }
                  : {}),
                ...(styleOverrides?.row ?? {}),
                ...(selected ? styleOverrides?.rowSelected ?? {} : {}),
                borderBottom:
                  i === rows.length - 1
                    ? "none"
                    : defaultStyles.row.borderBottom,
              }}
              onMouseEnter={(e) => {
                // ✅ hover não sobrescreve seleção
                if (selected) return;
                (e.currentTarget as HTMLDivElement).style.background = hoverBg;
              }}
              onMouseLeave={(e) => {
                if (selected) return;
                (e.currentTarget as HTMLDivElement).style.background =
                  "#ffffff";
              }}
              title={
                onRowClick
                  ? "Clique para selecionar. Ctrl/Cmd: múltiplo • Shift: intervalo"
                  : "Clique para selecionar"
              }
            >
              {r.map((c, j) => {
                const header = headers[j] ?? "";
                const custom =
                  perColumnRender && perColumnRender[header]
                    ? perColumnRender[header](r, i)
                    : null;

                const content = custom ?? c;
                const textAlign = centerSet.has(j) ? "center" : "left";

                return (
                  <div
                    key={j}
                    role="cell"
                    style={{
                      ...defaultStyles.cell,
                      textAlign,
                      borderRight:
                        j === headers.length - 1
                          ? "none"
                          : defaultStyles.cell.borderRight,
                      ...(styleOverrides?.cell ?? {}),
                    }}
                    title={
                      enableCellTitle ? safeTitle(content ?? "") : undefined
                    }
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ModalItemsGrid;
